import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Truck, ArrowRight, CheckCircle2, Clock, Globe, Shield, 
  Package, Zap, Phone, Mail, MapPin, Users, X, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';

const RoadFreightPage = () => {
  const { t } = useTranslation();

  // Structured data for Road Freight service
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Road Freight Coordination",
    "description": "FTL and groupage road freight coordination across Europe through qualified carriers.",
    "provider": {
      "@type": "Organization",
      "name": "Elementary Cargo B.V.",
      "url": "https://elementarycargo.com"
    },
    "serviceType": "Road Freight",
    "areaServed": "Europe"
  };

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Road Freight", path: "/services/road-freight" }
  ];

  const features = [
    { 
      icon: Users, 
      title: t('roadFreightPage.features.singleContact'), 
      description: t('roadFreightPage.features.singleContactDesc')
    },
    { 
      icon: Globe, 
      title: t('roadFreightPage.features.panEuropean'), 
      description: t('roadFreightPage.features.panEuropeanDesc')
    },
    { 
      icon: Shield, 
      title: t('roadFreightPage.features.documented'), 
      description: t('roadFreightPage.features.documentedDesc')
    },
    { 
      icon: MapPin, 
      title: t('roadFreightPage.features.doorToDoor'), 
      description: t('roadFreightPage.features.doorToDoorDesc')
    }
  ];

  const services = [
    {
      title: t('roadFreightPage.services.ftl'),
      description: t('roadFreightPage.services.ftlDesc'),
      features: t('roadFreightPage.services.ftlFeatures', { returnObjects: true })
    },
    {
      title: t('roadFreightPage.services.groupage'),
      description: t('roadFreightPage.services.groupageDesc'),
      features: t('roadFreightPage.services.groupageFeatures', { returnObjects: true })
    },
    {
      title: t('roadFreightPage.services.express'),
      description: t('roadFreightPage.services.expressDesc'),
      features: t('roadFreightPage.services.expressFeatures', { returnObjects: true })
    },
    {
      title: t('roadFreightPage.services.crossBorder'),
      description: t('roadFreightPage.services.crossBorderDesc'),
      features: t('roadFreightPage.services.crossBorderFeatures', { returnObjects: true })
    }
  ];

  const coverage = t('roadFreightPage.coverage', { returnObjects: true });
  const whatWeDo = t('roadFreightPage.whatWeDoList', { returnObjects: true });
  const whatWeDontDo = t('roadFreightPage.whatWeDoNotList', { returnObjects: true });

  const vehicles = [
    {
      type: t('roadFreightPage.vehicles.sprinter'),
      description: t('roadFreightPage.vehicles.sprinterDesc'),
      capacity: t('roadFreightPage.vehicles.sprinterCapacity'),
      icon: Truck
    },
    {
      type: t('roadFreightPage.vehicles.boxTruck'),
      description: t('roadFreightPage.vehicles.boxTruckDesc'),
      capacity: t('roadFreightPage.vehicles.boxTruckCapacity'),
      icon: Truck
    },
    {
      type: t('roadFreightPage.vehicles.curtainsider'),
      description: t('roadFreightPage.vehicles.curtainsiderDesc'),
      capacity: t('roadFreightPage.vehicles.curtainsiderCapacity'),
      icon: Truck
    },
    {
      type: t('roadFreightPage.vehicles.mega'),
      description: t('roadFreightPage.vehicles.megaDesc'),
      capacity: t('roadFreightPage.vehicles.megaCapacity'),
      icon: Truck
    }
  ];

  return (
    <div className="min-h-screen bg-offwhite" data-testid="road-freight-page">
      <SEO 
        titleKey="seo.roadFreight.title"
        descriptionKey="seo.roadFreight.description"
        path="/services/road-freight"
        structuredData={serviceSchema}
        breadcrumbs={breadcrumbs}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy-900 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1920&q=80"
            alt="Truck on highway - coordinated through our carrier partners"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/95 to-navy-900/80" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gold/20 rounded-xl flex items-center justify-center">
                <Truck className="w-7 h-7 text-gold" />
              </div>
              <span className="text-gold text-sm font-semibold tracking-wider uppercase">{t('roadFreightPage.badge')}</span>
            </div>
            <h1 className="font-space text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('roadFreightPage.heroTitle')}
            </h1>
            <p className="text-platinum-light text-lg md:text-xl leading-relaxed mb-8">
              {t('roadFreightPage.heroSubtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/quote" data-testid="road-freight-quote-btn">
                <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 font-semibold px-8 py-4 rounded-lg shadow-lg hover:shadow-xl hover:shadow-gold/25 transition-all">
                  {t('roadFreightPage.requestQuote')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-platinum/30 text-white hover:bg-white/10 px-8 py-4 rounded-lg">
                  {t('roadFreightPage.talkWithUs')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('roadFreightPage.whyCoordinateWithUs')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900">
              {t('roadFreightPage.structuredCoordination')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={`item-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border border-platinum-light hover:border-gold/30 hover:shadow-lg transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-xl bg-navy-900/5 flex items-center justify-center mx-auto mb-4">
                      <feature.icon className="w-6 h-6 text-gold" />
                    </div>
                    <h3 className="font-space text-lg font-bold text-navy-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-offwhite">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('roadFreightPage.whatWeCoordinate')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('roadFreightPage.roadFreightOptions')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('roadFreightPage.roadFreightOptionsDesc')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={`item-${index}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border border-platinum-light hover:border-gold/30 transition-colors">
                  <CardContent className="p-8">
                    <h3 className="font-space text-xl font-bold text-navy-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600 mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {Array.isArray(service.features) && service.features.map((feature) => (
                        <li key={`${service.title.slice(0, 10)}-${feature.slice(0, 15)}`} className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* European Coverage */}
      <section className="py-20 bg-navy-900">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
                {t('roadFreightPage.europeanCoverage')}
              </span>
              <h2 className="font-space text-3xl md:text-4xl font-bold text-white mb-6">
                {t('roadFreightPage.destinations')}
              </h2>
              <p className="text-platinum-light mb-8">
                {t('roadFreightPage.destinationsDesc')}
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {Array.isArray(coverage) && coverage.map((destination, index) => (
                  <div key={`item-${index}`} className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />
                    <span className="text-sm">{destination}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80"
                  alt="European road freight network"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vehicle Types */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('roadFreightPage.equipment')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900">
              {t('roadFreightPage.vehicleTypes')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {vehicles.map((vehicle, index) => (
              <motion.div
                key={`item-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-offwhite border border-platinum-light hover:border-gold/30 transition-colors">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-xl bg-navy-900/5 flex items-center justify-center mx-auto mb-4">
                      <vehicle.icon className="w-6 h-6 text-gold" />
                    </div>
                    <h3 className="font-space text-lg font-bold text-navy-900 mb-2">{vehicle.type}</h3>
                    <p className="text-slate-600 text-sm mb-2">{vehicle.description}</p>
                    <p className="text-gold font-semibold text-sm">{vehicle.capacity}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Transparency Section */}
      <section className="py-20 bg-offwhite">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('roadFreightPage.ourRole')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('roadFreightPage.clearAboutWhatWeDo')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('roadFreightPage.transparencyDesc')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-green-200 bg-green-50/50">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-space text-xl font-bold text-navy-900">{t('roadFreightPage.whatWeDo')}</h3>
                  </div>
                  <ul className="space-y-4">
                    {Array.isArray(whatWeDo) && whatWeDo.map((item, index) => (
                      <li key={`item-${index}`} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-slate-200 bg-slate-50/50">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-slate-400 flex items-center justify-center">
                      <X className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-space text-xl font-bold text-navy-900">{t('roadFreightPage.whatWeDoNot')}</h3>
                  </div>
                  <ul className="space-y-4">
                    {Array.isArray(whatWeDontDo) && whatWeDontDo.map((item, index) => (
                      <li key={`item-${index}`} className="flex items-start gap-3">
                        <X className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="font-space text-3xl md:text-4xl font-bold text-white mb-4">
              {t('roadFreightPage.needCoordination')}
            </h2>
            <p className="text-platinum-light text-lg mb-8">
              {t('roadFreightPage.needCoordinationDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/quote">
                <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 font-semibold px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl hover:shadow-gold/25 transition-all w-full sm:w-auto">
                  {t('roadFreightPage.requestAQuote')} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="flex items-center justify-center gap-6 text-platinum-light text-sm">
              <a href="mailto:info@elementarycargo.com" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Mail className="w-4 h-4" /> info@elementarycargo.com
              </a>
              <a href="tel:+31625381824" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Phone className="w-4 h-4" /> +31 62 538 1824
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RoadFreightPage;
